import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useComsAvailability } from '../context/ComsContext';
import NavDropdown from './NavDropdown';

// ── CommunitiesDropdown ───────────────────────────────────────────────────────
// Shown in the navbar when the user has 2+ active memberships.
const CommunitiesDropdown = ({ memberships }) => {
  const location   = useLocation();
  const [open, setOpen] = useState(false);
  const ref        = useRef(null);

  // Derive which community slug is currently active from the URL
  const tenantMatch = location.pathname.match(/^\/t\/([^/]+)/);
  const activeSlug  = tenantMatch ? tenantMatch[1] : null;

  // Close when clicking outside
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on route change
  useEffect(() => { setOpen(false); }, [location.pathname]);

  const activeMembership = memberships.find((m) => {
    const slug = m.tenantId?.slug || m.tenantSlug;
    return slug === activeSlug;
  });
  const label = activeMembership
    ? (activeMembership.tenantId?.branding?.displayName || activeMembership.tenantId?.name || activeMembership.tenantName || activeSlug)
    : 'My Communities';

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          font: 'inherit', color: 'inherit', padding: '0 4px',
          display: 'flex', alignItems: 'center', gap: 4,
        }}
      >
        {label}
        <span style={{ fontSize: '0.65rem', opacity: 0.7 }}>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 8px)', right: 0,
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)', minWidth: 220, zIndex: 999,
          overflow: 'hidden',
        }}>
          <div style={{ padding: '6px 14px 4px', fontSize: '0.7rem', fontWeight: 700,
            color: '#94a3b8', letterSpacing: 0.5, textTransform: 'uppercase' }}>
            Switch Community
          </div>
          {memberships.map((m) => {
            const slug    = m.tenantId?.slug || m.tenantSlug;
            const name    = m.tenantId?.branding?.displayName || m.tenantId?.name || m.tenantName || slug;
            const color   = m.tenantId?.branding?.primaryColor || '#4285F4';
            const isActive = slug === activeSlug;
            return (
              <Link
                key={m._id || slug}
                to={`/t/${slug}/`}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '10px 14px', textDecoration: 'none',
                  color: isActive ? color : '#1e293b',
                  background: isActive ? color + '11' : 'transparent',
                  fontWeight: isActive ? 700 : 400, fontSize: '0.9rem',
                }}
              >
                <span style={{
                  width: 8, height: 8, borderRadius: '50%',
                  background: color, flexShrink: 0, opacity: isActive ? 1 : 0.4,
                }} />
                {name}
              </Link>
            );
          })}
          <div style={{ borderTop: '1px solid #f1f5f9', padding: '6px 14px 8px' }}>
            <Link to="/dashboard" style={{ fontSize: '0.8rem', color: '#64748b', textDecoration: 'none' }}>
              ← All communities
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

const Navbar = ({ mode = 'platform', navItems = [] }) => {
  const { user, panel, activeTenant, memberships, logout, isModuleEnabled } = useAuth();
  const { comsEnabled } = useComsAvailability();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Determine brand link destination:
  // - If tenant has websiteUrl configured, link externally to it
  // - Otherwise, link to CIP home
  const brandHref = activeTenant?.branding?.websiteUrl || '/';
  const isExternalLink = !!activeTenant?.branding?.websiteUrl;
  const hasTenantBrand = mode === 'tenant' && Boolean(activeTenant?.branding?.logoUrl || activeTenant?.branding?.displayName || activeTenant?.name);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Platform staff/admin — nav items are passed in from the shell and rendered
  // inline in this same header row (single unified header, no separate sub-nav bar).
  const isPlatformPanel = panel === 'admin' || panel === 'staff';

  return (
    <nav className="navbar" data-panel={panel}>
      {mode === 'platform' ? (
        <Link to="/dashboard" className="navbar-brand" aria-label="CIP dashboard">
          <span className="navbar-brand-text">CIP</span>
        </Link>
      ) : hasTenantBrand && isExternalLink ? (
        <a href={brandHref} className="navbar-brand" title="Go to main website">
          {activeTenant?.branding?.logoUrl && (
            <img 
              src={activeTenant.branding.logoUrl} 
              alt={activeTenant.branding.displayName || 'Logo'} 
              className="navbar-logo"
            />
          )}
          <span className="navbar-brand-text">
            {activeTenant?.branding?.displayName || activeTenant?.name}
          </span>
        </a>
      ) : hasTenantBrand ? (
        <Link to={brandHref} className="navbar-brand">
          {activeTenant?.branding?.logoUrl && (
            <img 
              src={activeTenant.branding.logoUrl} 
              alt={activeTenant.branding.displayName || 'Logo'} 
              className="navbar-logo"
            />
          )}
          <span className="navbar-brand-text">
            {activeTenant?.branding?.displayName || activeTenant?.name}
          </span>
        </Link>
      ) : <div aria-hidden="true" />}
      <div className="navbar-right">
        <button
          type="button"
          className="cip-menu-toggle"
          aria-label="Toggle navigation menu"
          aria-expanded={mobileMenuOpen}
          onClick={() => setMobileMenuOpen((open) => !open)}
        >
          <span></span><span></span><span></span>
        </button>
        <div className={`navbar-links${mobileMenuOpen ? ' navbar-links--open' : ''}`} onClick={() => setMobileMenuOpen(false)}>
          {user ? (
            <>
              {isPlatformPanel && navItems.map((item) =>
                item.children ? (
                  <NavDropdown key={item.label} label={item.label} items={item.children} />
                ) : (
                  <NavLink key={item.to} to={item.to} end={item.to === '/dashboard'} className={({ isActive }) => isActive ? 'navbar-link--active' : undefined}>
                    {item.label}
                  </NavLink>
                )
              )}
              {comsEnabled && isModuleEnabled('coms') && panel === 'community' && (
                <Link to="/coms">Coms</Link>
              )}
              {!isPlatformPanel && (
                <>
                  {/* Multi-membership: show community switcher dropdown; otherwise plain Dashboard link */}
                  {(panel === 'user' || panel === 'community') && memberships?.length > 1
                    ? <CommunitiesDropdown memberships={memberships} />
                    : <Link to="/dashboard">Dashboard</Link>
                  }
                  {(panel === 'user' || panel === 'community') && (
                    <Link to="/membership">Membership</Link>
                  )}
                  {panel === 'community' && (
                    <Link to="/admin">Admin</Link>
                  )}
                </>
              )}
              <NavDropdown label="My account" items={[
                { to: '/account', label: 'Account settings' },
                { to: '/my-data', label: 'My data' },
              ]} />
              <button onClick={handleLogout}>Logout</button>
            </>
          ) : (
            <>
              <Link to="/login">Login</Link>
              <Link to="/register">Register</Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
